import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import { calendarEventsInDateRangeLoadedForLock } from '../../actions/eventsCacheActions';
import { containsDateRange, DateRange, dateRangesOverlap } from 'owa-datetime-utils';
import { EventsCacheLockId, getEventsFromCache } from 'owa-events-cache';
import { getCalendarEventsForDateRange } from './getCalendarEventsForDateRange';
import { loadCalendarEventsInDateRange } from '../../services/loadCalendarEventsInDateRange';
import { loadingCalendarEvents } from '../../actions/publicActions';
import { MAX_RETRIES_TO_LOAD_EVENTS_FOR_DATERANGE } from '../../constants';
import {
    getEventsCacheLockInfo,
    getAllLockedDateRanges,
    getEventsCache,
} from '../../selectors/eventsCacheSelectors';
import { logCacheUpdateForDiagnosticsAsync } from 'owa-calendar-cache-diagnostics';

interface FetchingCalendarEventsInfo {
    dateRange: DateRange;
    requestPromise: Promise<CalendarEvent[]>;
}

const fetchingCalendarEventsInfoMap: { [folderId: string]: FetchingCalendarEventsInfo[] } = {};

function tryInitializeInfoList(folderId: string) {
    if (!fetchingCalendarEventsInfoMap[folderId]) {
        fetchingCalendarEventsInfoMap[folderId] = [];
    }
}

function findFetchingCalendarEventsInfo(
    folderId: string,
    dateRange: DateRange
): { index: -1; info: null } | { index: number; info: FetchingCalendarEventsInfo } {
    tryInitializeInfoList(folderId);

    const infoList = fetchingCalendarEventsInfoMap[folderId];
    for (let i = 0; i < infoList.length; i++) {
        if (containsDateRange(infoList[i].dateRange, dateRange, true /* inclusive */)) {
            return { index: i, info: infoList[i] };
        }
    }

    return { index: -1, info: null };
}

function startedFetchingCalendarEvents(
    folderId: string,
    dateRange: DateRange,
    fetchPromise: Promise<CalendarEvent[]>
) {
    tryInitializeInfoList(folderId);

    fetchingCalendarEventsInfoMap[folderId].push({
        dateRange: dateRange,
        requestPromise: fetchPromise,
    });
}

function endedFetchingCalendarEvents(folderId: string, dateRange: DateRange) {
    const { index } = findFetchingCalendarEventsInfo(folderId, dateRange);

    if (index !== -1) {
        fetchingCalendarEventsInfoMap[folderId].splice(index, 1);
    }
}

export function getDateRangesBeingFetchedForFolder(folderId: string) {
    tryInitializeInfoList(folderId);

    return fetchingCalendarEventsInfoMap[folderId].map(info => info.dateRange);
}

export function fetchCalendarEventsForDateRange(
    lockId: EventsCacheLockId,
    folderId: string,
    dateRange: DateRange
): { fetchPromise: Promise<CalendarEvent[]>; events: CalendarEvent[] | null } {
    const lockInfo = getEventsCacheLockInfo(lockId, folderId);

    if (
        !lockInfo ||
        !lockInfo.lockedDateRange ||
        !containsDateRange(lockInfo.lockedDateRange, dateRange, true /* inclusive */)
    ) {
        const eventsFromCache = getEventsInDateRangeFromCacheIfExists(lockId, folderId, dateRange);

        if (eventsFromCache) {
            // Events for the requested daterange were present in the cache from another lock
            const promise = handleCalendarEventsLoaded(
                lockId,
                folderId,
                dateRange,
                Promise.resolve(eventsFromCache),
                'existing'
            );
            return { fetchPromise: promise, events: eventsFromCache };
        }

        // If the lock does not have any events date range locked or the range
        // doesn't contain the requested date range or if there are no other locks
        // which are a superset of requested range, then fetch more from server.

        const { info: currentFetchingCalendarEventsInfo } = findFetchingCalendarEventsInfo(
            folderId,
            dateRange
        );

        if (currentFetchingCalendarEventsInfo) {
            // If there is already a request going for the same date range
            // then return the same request
            const promise = handleCalendarEventsLoaded(
                lockId,
                folderId,
                dateRange,
                currentFetchingCalendarEventsInfo.requestPromise.then(events =>
                    // We filter the events to only ones that overlap with the requested
                    // date range because the existing request might have date range
                    // equal or bigger than the currently one existed.
                    events.filter(
                        ({ Start, End }) =>
                            dateRangesOverlap(
                                dateRange,
                                { start: Start, end: End },
                                true /* inclusive */
                            ) == 0
                    )
                ),
                'new'
            );
            return { fetchPromise: promise, events: null };
        }

        const calendarEventsFetchPromise = fetchCalendarEventsFromServer(folderId, dateRange);
        const promise = handleCalendarEventsLoaded(
            lockId,
            folderId,
            dateRange,
            calendarEventsFetchPromise,
            'new'
        );
        return { fetchPromise: promise, events: null };
    } else {
        // The date range requested already exists in the cache so return that
        const eventsForDateRange = getCalendarEventsForDateRange(lockId, folderId, dateRange);
        return { fetchPromise: Promise.resolve(eventsForDateRange), events: eventsForDateRange };
    }
}

function fetchCalendarEventsFromServer(folderId: string, dateRange: DateRange) {
    const fetchPromise = (async () => {
        try {
            return await loadCalendarEventsInDateRange(
                dateRange,
                folderId,
                loadingCalendarEvents,
                MAX_RETRIES_TO_LOAD_EVENTS_FOR_DATERANGE
            );
        } finally {
            endedFetchingCalendarEvents(folderId, dateRange);
        }
    })();

    startedFetchingCalendarEvents(folderId, dateRange, fetchPromise);

    return fetchPromise;
}

async function handleCalendarEventsLoaded(
    lockId: EventsCacheLockId,
    folderId: string,
    dateRange: DateRange,
    fetchPromise: Promise<CalendarEvent[]>,
    diagnosticMessageSuffix: 'existing' | 'new'
): Promise<CalendarEvent[]> {
    const result = await fetchPromise;

    // Log to diagnostics panel
    const diagnosticMessageItemsLength = result ? result.length : 'no';
    logCacheUpdateForDiagnosticsAsync({
        lockId: lockId,
        updateMessage: `Fetched ${diagnosticMessageItemsLength} ${diagnosticMessageSuffix} items for date range: ${
            dateRange.start
        } - ${dateRange.end}`,
    });

    if (result) {
        calendarEventsInDateRangeLoadedForLock(lockId, folderId, dateRange, result);
    }

    return result;
}

function getEventsInDateRangeFromCacheIfExists(
    lockId: EventsCacheLockId,
    folderId: string,
    dateRange: DateRange
) {
    const allDateRanges = getAllLockedDateRanges(folderId);
    const cache = getEventsCache(folderId);
    const dateRangePresentInCache = allDateRanges.some(dateRangeEntry =>
        containsDateRange(dateRangeEntry, dateRange, true)
    );

    if (dateRangePresentInCache && cache) {
        return (
            // return a copy of the events here, so that exists events are not modified
            getEventsFromCache(dateRange, cache.events).map(event => ({ ...event }))
        );
    } else {
        return null;
    }
}
